import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNotifications } from './NotificationContext';
import ComingUpList from './profile/ComingUpList.jsx';
import ChannelPlatformTable from './profile/ChannelPlatformTable.jsx';
import { isUpcomingEventSubscription } from './profile/profileUtils.js';
import '../styles/notificationsTable.css';
import '../styles/profilePage.css';

const FALLBACK_TIMEZONES = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Toronto',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Australia/Sydney',
    'UTC'
];

const getSupportedTimezones = () => {
    if (typeof Intl.supportedValuesOf === 'function') {
        try {
            return Intl.supportedValuesOf('timeZone');
        } catch {
            // Fall through
        }
    }
    return FALLBACK_TIMEZONES;
};

const PLATFORM_LABELS = {
    youtube: 'YouTube',
    twitch: 'Twitch',
    kick: 'Kick'
};

const PLATFORM_ORDER = ['youtube', 'twitch', 'kick'];

function groupChannelsByPlatform(channels) {
    const map = new Map();
    (channels || []).forEach((channel) => {
        const key = channel.platform || 'other';
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(channel);
    });
    const known = PLATFORM_ORDER.filter((key) => map.has(key));
    const extra = [...map.keys()].filter((key) => !PLATFORM_ORDER.includes(key)).sort();
    return [...known, ...extra].map((platform) => ({
        platform,
        label: PLATFORM_LABELS[platform] || platform,
        channels: map.get(platform),
    }));
}

const ActiveToggle = ({ checked, onChange, disabled, label }) => (
    <label className={`active-toggle${disabled ? ' active-toggle--disabled' : ''}`}>
        <input
            type="checkbox"
            className="active-toggle-input"
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            aria-label={label}
        />
        <span className="active-toggle-track" aria-hidden="true">
            <span className="active-toggle-thumb" />
        </span>
    </label>
);

const NotificationPreferences = ({ highlightChannels = false }) => {
    const {
        subscriptions,
        preferences,
        preferencesError,
        platformChannels,
        platformChannelsError,
        platformChannelsLoading,
        permissionStatus,
        subscriptionsLoading,
        preferencesSaving,
        error,
        refreshPreferences,
        requestPermission,
        unsubscribe,
        setChannelAlertsEnabled,
        updatePreferences: updatePrefs
    } = useNotifications();

    const [localPreferences, setLocalPreferences] = useState({
        emailOptIn: false,
        webPushOptIn: true,
        quietHoursStart: null,
        quietHoursEnd: null,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        weeklyDigestEnabled: false
    });
    const [saveError, setSaveError] = useState(null);
    const [permissionError, setPermissionError] = useState(null);
    const [channelToggleError, setChannelToggleError] = useState(null);
    const [bulkBusy, setBulkBusy] = useState(null);
    const channelsRef = useRef(null);

    const timezones = useMemo(getSupportedTimezones, []);
    const [timezoneInput, setTimezoneInput] = useState(localPreferences.timezone);

    useEffect(() => {
        if (preferences) {
            const loaded = {
                emailOptIn: preferences.emailOptIn ?? false,
                webPushOptIn: preferences.webPushOptIn ?? true,
                quietHoursStart: preferences.quietHoursStart ?? null,
                quietHoursEnd: preferences.quietHoursEnd ?? null,
                timezone: preferences.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
                weeklyDigestEnabled: preferences.weeklyDigestEnabled ?? false
            };
            setLocalPreferences(loaded);
            setTimezoneInput(loaded.timezone);
        }
    }, [preferences]);

    useEffect(() => {
        if (!highlightChannels || !channelsRef.current) return;
        channelsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [highlightChannels]);

    const preferencesUnavailable = !preferences;
    const upcoming = (subscriptions || []).filter(isUpcomingEventSubscription);
    const channelGroups = useMemo(
        () => groupChannelsByPlatform(platformChannels),
        [platformChannels]
    );

    const persist = async (next) => {
        if (preferencesUnavailable) return;
        setLocalPreferences(next);
        try {
            setSaveError(null);
            await updatePrefs(next);
        } catch (err) {
            setSaveError(err.message || 'Failed to save preferences');
        }
    };

    const handleTimezoneInput = (value) => {
        setTimezoneInput(value);
        if (timezones.includes(value)) {
            persist({ ...localPreferences, timezone: value });
        }
    };

    const timezoneInvalid = timezoneInput !== '' && !timezones.includes(timezoneInput);
    const hourOptions = Array.from({ length: 24 }, (_, i) => i);

    const handleToggleChannelAlerts = async (channelId, nextEnabled) => {
        try {
            setChannelToggleError(null);
            await setChannelAlertsEnabled(channelId, nextEnabled);
        } catch (err) {
            setChannelToggleError('Failed to update channel alerts: ' + (err.message || 'Unknown error'));
        }
    };

    const handleSetPlatformAll = async (group, nextEnabled) => {
        setBulkBusy(group.platform);
        setChannelToggleError(null);
        try {
            if (nextEnabled) {
                const permission = await requestPermission();
                if (permission !== 'granted') {
                    setChannelToggleError('Browser notification permission is required for live alerts.');
                    return;
                }
            }
            const targets = group.channels.filter(
                (channel) => (channel.alertsEnabled !== false) !== nextEnabled
            );
            await Promise.all(
                targets.map((channel) => setChannelAlertsEnabled(channel.id, nextEnabled))
            );
        } catch (err) {
            setChannelToggleError('Failed to update channel alerts: ' + (err.message || 'Unknown error'));
        } finally {
            setBulkBusy(null);
        }
    };

    const handleRequestPermission = async () => {
        try {
            setPermissionError(null);
            const permission = await requestPermission();
            if (permission !== 'granted') {
                setPermissionError(
                    permission === 'denied'
                        ? 'Notification permission denied. Enable it in your browser settings to receive live alerts.'
                        : 'Notification permission was not granted. Live alerts require browser notifications.'
                );
            }
        } catch (err) {
            setPermissionError(
                'Failed to enable notifications: ' + (err.message || 'Could not register this device for web push.')
            );
        }
    };

    return (
        <div className="notifications-section">
            <div className="profile-section-head">
                <h3>Watching</h3>
                <span>Upcoming only</span>
            </div>
            <p className="profile-hint">Events you asked to be reminded about.</p>
            {subscriptionsLoading ? (
                <p className="profile-empty">Loading reminders...</p>
            ) : (
                <ComingUpList
                    items={upcoming}
                    onRemove={unsubscribe}
                    emptyHint="No upcoming reminders. Use Notify on an event to add one here."
                />
            )}

            <div className="profile-section-head">
                <h3>Delivery</h3>
                <span>{preferencesSaving ? 'Saving...' : 'Auto-saves'}</span>
            </div>

            {permissionStatus !== 'granted' && permissionStatus !== 'unsupported' && (
                <div className={`permission-status ${permissionStatus === 'granted' ? 'granted' : ''}`}>
                    <p>
                        {permissionStatus === 'denied'
                            ? 'Notification permission denied. Please enable it in your browser settings.'
                            : 'Enable browser notifications to receive alerts on this device.'}
                    </p>
                    {permissionStatus !== 'denied' && (
                        <button className="profile-btn profile-btn--primary" onClick={handleRequestPermission}>
                            Enable Notifications
                        </button>
                    )}
                </div>
            )}
            {permissionError ? <p className="profile-error">{permissionError}</p> : null}
            {preferencesError ? (
                <div className="profile-error" role="alert">
                    Could not load preferences: {preferencesError}{' '}
                    <button type="button" className="profile-text-btn" onClick={refreshPreferences}>Retry</button>
                </div>
            ) : null}

            <div className="profile-settings-row">
                <div className="profile-settings-copy">
                    <div className="profile-settings-title">Push on this device</div>
                    <div className="profile-settings-hint">
                        {permissionStatus === 'granted' ? 'Browser permission granted' : 'Requires browser permission'}
                    </div>
                </div>
                <ActiveToggle
                    checked={localPreferences.webPushOptIn}
                    disabled={preferencesUnavailable || permissionStatus !== 'granted'}
                    label="Push on this device"
                    onChange={(e) => persist({ ...localPreferences, webPushOptIn: e.target.checked })}
                />
            </div>

            <div className="profile-settings-row">
                <div className="profile-settings-copy">
                    <div className="profile-settings-title">Quiet hours</div>
                    <div className="profile-quiet">
                        <select
                            aria-label="Quiet hours start"
                            value={localPreferences.quietHoursStart ?? ''}
                            disabled={preferencesUnavailable}
                            onChange={(e) => persist({
                                ...localPreferences,
                                quietHoursStart: e.target.value === '' ? null : parseInt(e.target.value, 10)
                            })}
                        >
                            <option value="">Off</option>
                            {hourOptions.map((hour) => (
                                <option key={hour} value={hour}>{`${String(hour).padStart(2, '0')}:00`}</option>
                            ))}
                        </select>
                        <span>to</span>
                        <select
                            aria-label="Quiet hours end"
                            value={localPreferences.quietHoursEnd ?? ''}
                            disabled={preferencesUnavailable}
                            onChange={(e) => persist({
                                ...localPreferences,
                                quietHoursEnd: e.target.value === '' ? null : parseInt(e.target.value, 10)
                            })}
                        >
                            <option value="">Off</option>
                            {hourOptions.map((hour) => (
                                <option key={hour} value={hour}>{`${String(hour).padStart(2, '0')}:00`}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className="profile-settings-row">
                <div className="profile-settings-copy">
                    <div className="profile-settings-title">Timezone</div>
                    <input
                        className="timezone-select"
                        type="text"
                        list="timezone-options"
                        value={timezoneInput}
                        onChange={(e) => handleTimezoneInput(e.target.value)}
                        onBlur={() => setTimezoneInput(localPreferences.timezone)}
                        placeholder="Search timezones..."
                        autoComplete="off"
                        disabled={preferencesUnavailable}
                        aria-invalid={timezoneInvalid}
                    />
                    <datalist id="timezone-options">
                        {timezones.map((tz) => (
                            <option key={tz} value={tz} />
                        ))}
                    </datalist>
                    {timezoneInvalid ? <span className="profile-settings-hint">Pick a timezone from the list</span> : null}
                </div>
            </div>
            {saveError ? <p className="profile-error">{saveError}</p> : null}

            <div
                ref={channelsRef}
                className={highlightChannels ? 'profile-channels-highlight' : undefined}
            >
                <div className="profile-section-head">
                    <h3>Synced channels</h3>
                    <span>{platformChannels.length}</span>
                </div>
                <p className="profile-hint">
                    Followed on YouTube, Twitch, or Kick creators you follow in LiveNow. Mute without unfollowing.
                </p>
                {platformChannelsLoading ? (
                    <p className="profile-empty">Loading channels...</p>
                ) : null}
                {!platformChannelsLoading && platformChannels.length === 0 ? (
                    <p className="profile-empty">Connect YouTube, Twitch, or Kick in Account to sync channels here.</p>
                ) : null}
                {!platformChannelsLoading && platformChannels.length > 0 ? (
                    <div className="profile-channel-tables">
                        {channelGroups.map((group) => (
                            <ChannelPlatformTable
                                key={group.platform}
                                platformLabel={group.label}
                                channels={group.channels}
                                busy={bulkBusy === group.platform}
                                onToggle={handleToggleChannelAlerts}
                                onSetAll={(next) => handleSetPlatformAll(group, next)}
                            />
                        ))}
                    </div>
                ) : null}
                {channelToggleError ? <p className="profile-error">{channelToggleError}</p> : null}
                {platformChannelsError ? <p className="profile-error">{platformChannelsError}</p> : null}
                {error ? <p className="profile-error">{error}</p> : null}
            </div>
        </div>
    );
};

export default NotificationPreferences;
